import type {
  DraftAddress,
  DraftJobData,
  DraftJobLeg,
  DraftJobPassenger,
  VehicleTypeItem,
} from '../../../api/dispatch';
import type { CrmContact } from '../../../api/types';
import {
  type BookingFormState,
  type LegState,
  parseCount,
  parseLuggageCount,
  pickupAirportCode,
} from './bookingForm';
import { wallTimeToUtcIso } from './payload';

/**
 * Form state → the `jobData` blob dispatch parks as a draft: the incomplete
 * twin of buildJobPayload, and a DIFFERENT shape from it. A job is validated
 * and normalised by dispatch-be; a draft is opaque jsonb that only the
 * dashboard's Add Job form ever reads back, so this file mirrors that form's
 * own model (`job_draft_mapper.dart` → `DraftJobData`) rather than the job wire.
 *
 * Nothing here is validated or dropped for being incomplete — capturing an
 * unfinished booking IS the feature. Unresolved addresses keep their raw text,
 * a missing pickup time stays null, and vehicle 0 means "not chosen yet",
 * exactly as when a dispatcher parks a half-filled Add Job.
 */

/** The dashboard's kDraftJobDefaultExpiry — re-parkable from its Drafts list. */
export const DRAFT_EXPIRY_DAYS = 30;

export const draftExpiresAt = (now: Date = new Date()): string =>
  new Date(now.getTime() + DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString();

/**
 * Marks a draft this console parked, beside the dashboard's own `addJob` and
 * `vehicleRates`. It is stored, searchable and ignored: the Drafts list labels
 * any source it does not recognise "from: Add Job" until the dashboard learns
 * this one.
 */
export const DRAFT_SOURCE_CRM = 'crm';

const EMPTY_ADDRESS: DraftAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  addressType: 'N',
  latitude: 0,
  longitude: 0,
  placeId: '',
  zoneId: null,
};

/**
 * An unresolved leg still ships its raw text in addressLine1 — the address the
 * AI read out of the thread is the most valuable thing on a parked booking, and
 * Add Job shows it back in the field for the dispatcher to resolve.
 */
const draftAddress = (leg: LegState): DraftAddress =>
  leg.resolved
    ? {
        addressLine1: leg.resolved.addressLine1 || '',
        addressLine2: leg.resolved.addressLine2 || '',
        city: leg.resolved.city || '',
        state: leg.resolved.state || '',
        country: leg.resolved.country || '',
        postalCode: leg.resolved.postalCode || '',
        addressType: leg.resolved.addressType === 'A' ? 'A' : 'N',
        latitude: leg.resolved.latitude,
        longitude: leg.resolved.longitude,
        placeId: leg.resolved.placeId || '',
        zoneId: leg.resolved.zoneId ?? null,
      }
    : { ...EMPTY_ADDRESS, addressLine1: leg.text.trim() };

const emptyPassenger = (): DraftJobPassenger => ({
  id: 0,
  customerId: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
});

export interface DraftContext {
  /** The CRM conversation display id, stored for provenance. */
  conversationId: number;
  /** The thread's contact — who the dispatch customer link points at. */
  contact: CrmContact | null;
  /** The chosen catalogue row, or null while none is picked. */
  vehicle: VehicleTypeItem | null;
}

export function buildDraftJobData(form: BookingFormState, ctx: DraftContext): DraftJobData {
  const pickupIso = form.pickupDatetime
    ? wallTimeToUtcIso(form.pickupDatetime, form.timeZoneId)
    : null;
  const hourly = form.serviceType === 'hourly';

  const leg = (
    source: LegState,
    kind: 'P' | 'S' | 'D',
    seq: number,
    extra: { sameAsPickup?: boolean } = {},
  ): DraftJobLeg => {
    const isPickup = kind === 'P';
    // The flight details this form captures belong to the arrival pickup, and
    // the draft's field names are the FORM's, not the job wire's: airlineIata
    // holds the airline, flightIata the flight number.
    const airportCode = isPickup ? pickupAirportCode(form.pickup) : '';
    const flightIata = isPickup ? form.flightNumber.trim() : '';
    return {
      address: draftAddress(source),
      type: kind,
      // Which tab Add Job reopens the leg on, by the dashboard's own rule
      // (`_kindFromModel`): an airport code or a flight, never the address
      // type alone — a dropoff at JFK with neither belongs on the Address tab,
      // where the address the CRM stored is what shows.
      locationKind: airportCode || flightIata ? 'airport' : 'address',
      seq,
      addressId: null,
      airportCode,
      airlineIata: isPickup ? form.airline.trim() : '',
      flightIata,
      flightNumber: '',
      flight: null,
      timeIn: isPickup ? pickupIso : null,
      arriveAirportAt: 'At pickup time',
      waitTimeMinutes: null,
      sameAsPickup: extra.sameAsPickup === true,
    };
  };

  const first = form.passengerFirstName.trim();
  const last = form.passengerLastName.trim();
  const email = form.passengerEmail.trim().toLowerCase();
  const phone = form.passengerPhone.trim();

  // The same three shapes the job payload sends, and each account block is only
  // ever built from a row dispatch gave us — a passenger the agent typed is not
  // evidence of an account, and never was.
  const affiliate = form.bookerType === 'affiliate' ? form.bookerAffiliate : null;
  const customer = form.bookerType === 'customer' ? form.bookerCustomer : null;
  const stops = form.stops.filter((s) => s.text.trim() || s.resolved);

  return {
    version: 1,
    source: DRAFT_SOURCE_CRM,
    savedAt: new Date().toISOString(),
    quotedFare: null,
    sendNotification: form.sendNotifications,
    timezone: form.timeZoneId ?? '',
    voucher: '',
    // Parked for every booker type, unlike the job payload's conditional key:
    // a draft that drops it loses the affiliate's own number the moment the
    // agent switches tabs to check something.
    requesterResNo: form.bookerReference.trim(),
    account: {
      type: affiliate ? 'affiliate' : customer ? 'customer' : 'guest',
      accountNumber: affiliate?.id ?? customer?.id ?? null,
      customer: customer
        ? {
            id: customer.id,
            name: customer.name?.trim() || ctx.contact?.name?.trim() || '',
            email: customer.email?.trim().toLowerCase() || ctx.contact?.email?.trim().toLowerCase() || '',
            phone: customer.phone?.trim() || ctx.contact?.phoneNumber?.trim() || '',
            priority: 1,
            isBillingAccount: false,
            note: '',
          }
        : null,
      affiliate: affiliate
        ? {
            id: affiliate.id,
            companyName: affiliate.companyName ?? '',
            email: affiliate.email ?? '',
            phone: affiliate.phone ?? '',
            primaryContactName: affiliate.primaryContactName ?? '',
            primaryContactEmail: affiliate.primaryContactEmail ?? '',
            primaryContactPhone: affiliate.primaryContactPhone ?? '',
          }
        : null,
      affiliateValue: 0,
    },
    trip: {
      type: hourly ? 'H' : 'O',
      hours: hourly ? Number(form.durationHours) || null : null,
      passengerCount: parseCount(form.passengerCount),
      luggageCount: parseLuggageCount(form.luggageCount),
      vehicleTypeId: form.vehicleTypeId,
      // Only the row that matches what is selected — a stale one would repaint
      // the reopened form with a vehicle nobody chose.
      vehicle: ctx.vehicle && ctx.vehicle.id === form.vehicleTypeId ? ctx.vehicle : null,
    },
    primaryPassenger: { id: 0, customerId: null, firstName: first, lastName: last, email, phone },
    secondaryPassenger: emptyPassenger(),
    pickup: leg(form.pickup, 'P', 0),
    // Hourly "same as pickup" parks the EMPTY dropoff plus the flag, not a copy
    // of the pickup: the flag is what Add Job reopens the checkbox from, and it
    // re-serializes the pickup itself when the draft is finally booked.
    dropOff: leg(form.dropoff, 'D', 100, {
      sameAsPickup: hourly && form.dropoffSameAsPickup,
    }),
    stops: stops.map((s, index) => leg(s, 'S', index + 1)),
    addOns: {
      childSeats: [],
      tripProtection: false,
      travelWithPet: false,
      meetAndGreet: false,
      meetAndGreetType: 'inside',
      meetAndGreetText: '',
    },
    // The booker, resolved exactly as on a job save: the named account answers
    // for itself, and the typed guest fields only when nobody is named.
    booked: {
      ...(affiliate
        ? {
            name: affiliate.companyName ?? '',
            email: (affiliate.email ?? affiliate.primaryContactEmail ?? '').toLowerCase(),
            phone: affiliate.phone ?? affiliate.primaryContactPhone ?? '',
          }
        : customer
          ? {
              name: customer.name ?? '',
              email: (customer.email ?? '').toLowerCase(),
              phone: customer.phone ?? '',
            }
          : {
              name: form.bookerName.trim(),
              email: form.bookerEmail.trim().toLowerCase(),
              phone: form.bookerPhone.trim(),
            }),
      billingName: '',
      billingCompanyName: '',
    },
    notes: { customer: form.customerNote.trim(), driver: form.chauffeurNote.trim() },
    crmConversationId: ctx.conversationId,
    crmSource: true,
  };
}
